// ─── src/MeetupFeedbacksView.tsx ─────────────────────────────────────────────
//
// One place to see how the last meetup went:
//   * Averages for the four star ratings + NPS (industry-standard score).
//   * A table of every submission with the textual answers, plus CSV.

import { useMemo, useState } from "react";
import {
  Download, Trash2, MessageCircle, Star, Users, Quote,
  Copy, Check, ExternalLink,
} from "lucide-react";
import {
  useMeetupFeedbacks,
  useMeetupFeedbackStats,
  useDeleteMeetupFeedback,
} from "./hooks/useMeetupFeedbacks";
import { useMeetupEvents } from "./hooks/useMeetupEvents";
import { StatCard } from "./components/ui/StatCard";
import { TableSkeleton } from "./components/ui/Skeleton";
import type { MeetupFeedback } from "./api/meetupFeedbacks";

// ── CSV export ───────────────────────────────────────────────────────────────
// Textual answers are exported so the community team can copy quotes into
// social posts / newsletters without opening a modal per row.
function exportCsv(rows: MeetupFeedback[]) {
  const esc = (v: unknown) => {
    const s = v == null ? "" : String(v);
    return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
  };
  const header = [
    "Source", "Attendee", "Email", "Governorate",
    "Overall (1–5)", "Venue/Org (1–5)", "Speakers (1–5)", "Topic (1–5)",
    "NPS (0–10)",
    "Most useful part", "Wanted topics",
    "Quote consent", "Quote",
    "Heard about via", "Heard about other",
    "Should have attended", "Improvement",
    "Submitted at",
  ].join(",");
  const body = rows.map((r) =>
    [
      r.attendeeId ? "Token" : "Public",
      r.attendee?.fullName ?? "",
      // For token rows this is the attendee's registered email; for
      // public rows it's the email typed into the form.
      r.attendee?.email ?? r.respondentEmail ?? "",
      r.attendee?.governorate ?? "",
      r.ratingOverall, r.ratingVenueOrganization, r.ratingSpeakers, r.ratingTopic,
      r.nps,
      r.mostUsefulPart, r.wantedTopics ?? "",
      r.quoteConsent ? "Yes" : "No", r.quote ?? "",
      r.hearAbout, r.hearAboutOther ?? "",
      r.shouldHaveAttended ?? "", r.improvement ?? "",
      r.createdAt,
    ].map(esc).join(","),
  );
  const blob = new Blob(["﻿" + [header, ...body].join("\n")], {
    type: "text/csv;charset=utf-8;",
  });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `meetup_feedback_${new Date().toISOString().slice(0, 10)}.csv`;
  a.click();
  URL.revokeObjectURL(url);
}

// ── Utilities for pretty-printing ratings and NPS bands ──────────────────────
const ratingBar = (avg: number) => {
  const pct = Math.max(0, Math.min(1, avg / 5)) * 100;
  return (
    <div className="mt-1 h-1.5 w-full rounded-full bg-brand-navy overflow-hidden">
      <div
        className="h-full bg-brand-green rounded-full transition-[width]"
        style={{ width: `${pct}%` }}
      />
    </div>
  );
};

const hearAboutLabel = (key: string): string => {
  switch (key) {
    case "friend":         return "A friend";
    case "facebook_event": return "Facebook event";
    case "instagram":      return "Instagram";
    case "whatsapp_group": return "WhatsApp group";
    case "other":          return "Other";
    default:               return key;
  }
};

// ── View ─────────────────────────────────────────────────────────────────────

export default function MeetupFeedbacksView() {
  // Pick a specific event to scope stats + table. Empty string = all events.
  const [eventId, setEventId] = useState<string>("");
  const [detail, setDetail] = useState<MeetupFeedback | null>(null);
  const [copied, setCopied] = useState(false);

  // Base URL for the public feedback link. Set `REACT_APP_PUBLIC_SITE_URL`
  // to point at the mainwebsite deployment; falls back to a hardcoded
  // production URL if the env var isn't set, so ops always gets a valid
  // shareable link rather than a "localhost" one leaking into WhatsApp.
  const publicBase =
    process.env.REACT_APP_PUBLIC_SITE_URL || "https://startupsyria.org";
  const publicLink = eventId
    ? `${publicBase.replace(/\/+$/, "")}/meetup-feedback/public/${eventId}`
    : "";

  const copyPublicLink = async () => {
    if (!publicLink) return;
    try {
      await navigator.clipboard.writeText(publicLink);
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    } catch {
      // Old browsers or a locked-down clipboard — fall back to the URL bar.
      window.prompt("Copy this link:", publicLink);
    }
  };

  const { data: events } = useMeetupEvents({ page: 1, limit: 100 });
  const { data: stats } = useMeetupFeedbackStats(eventId || undefined);
  const { data: listData, isLoading, isError } = useMeetupFeedbacks(
    eventId || undefined,
  );

  const rows = useMemo(() => listData?.data ?? [], [listData]);

  const { mutate: deleteRow, isPending: isDeleting } = useDeleteMeetupFeedback();

  const selectedEvent = useMemo(
    () => events?.data.find((e) => e.id === eventId),
    [events, eventId],
  );

  return (
    <div className="space-y-6">
      {/* Event picker + CSV ─────────────────────────────────────────── */}
      <div className="card p-4 flex flex-col sm:flex-row sm:items-end gap-3">
        <div className="flex-1">
          <label className="block text-xs uppercase tracking-wide text-brand-muted mb-1.5">
            Meetup
          </label>
          <select
            value={eventId}
            onChange={(e) => setEventId(e.target.value)}
            className="w-full py-2 px-3 text-sm bg-brand-navy border border-brand-border rounded-lg text-white focus:outline-none focus:border-brand-green"
          >
            <option value="">All meetups</option>
            {events?.data.map((ev) => (
              <option key={ev.id} value={ev.id}>
                {ev.title} · {ev.eventDate}
              </option>
            ))}
          </select>
        </div>

        <button
          type="button"
          onClick={() => exportCsv(rows)}
          disabled={rows.length === 0}
          className="inline-flex items-center gap-2 h-10 px-4 rounded-lg bg-brand-navy border border-brand-border text-white text-sm font-semibold disabled:opacity-40"
        >
          <Download size={14} />
          CSV
        </button>
      </div>

      {/* Public feedback link — shareable per-meetup URL that doesn't rely
          on a ticket token. Only surfaces once a specific meetup is
          picked; otherwise the link doesn't identify a single event. */}
      {eventId && (
        <div className="card p-4 space-y-2">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
            <div>
              <p className="text-xs uppercase tracking-wide text-brand-muted">
                Public feedback link
              </p>
              <p className="text-xs text-brand-muted mt-0.5">
                Anyone with this link can leave feedback for this meetup — WhatsApp broadcast, Instagram story, a printed QR at the venue.
              </p>
            </div>
            <div className="flex items-center gap-2 shrink-0">
              <button
                type="button" onClick={copyPublicLink}
                className="inline-flex items-center gap-1.5 h-9 px-3 rounded-lg bg-brand-navy border border-brand-border text-white text-xs font-semibold hover:border-brand-green"
              >
                {copied ? <Check size={13} className="text-brand-green" /> : <Copy size={13} />}
                {copied ? "Copied" : "Copy link"}
              </button>
              <a
                href={publicLink} target="_blank" rel="noopener noreferrer"
                className="inline-flex items-center gap-1.5 h-9 px-3 rounded-lg bg-brand-navy border border-brand-border text-white text-xs font-semibold hover:border-brand-green"
              >
                <ExternalLink size={13} />
                Open
              </a>
            </div>
          </div>
          <div className="rounded-lg bg-brand-navy border border-brand-border px-3 py-2 text-xs text-brand-muted font-mono truncate" title={publicLink}>
            {publicLink}
          </div>
        </div>
      )}

      {/* Stat cards ───────────────────────────────────────────────── */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <StatCard label="Responses" value={stats?.total ?? 0} icon={MessageCircle} accent="bg-brand-orange" />
        <StatCard label="Avg. overall" value={stats?.averageOverall.toFixed(1) ?? "0.0"} icon={Star} accent="bg-brand-green" />
        <StatCard label="NPS score" value={stats?.npsScore ?? 0} icon={Users} accent="bg-brand-teal" />
        <StatCard label="Quotes consented" value={stats?.quotesConsented ?? 0} icon={Quote} accent="bg-brand-teal" />
      </div>

      {/* Rating averages block ────────────────────────────────────── */}
      <div className="card p-5 space-y-4">
        <h3 className="text-sm font-semibold text-white">Average ratings</h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-4">
          <RatingRow label="Overall experience"       avg={stats?.averageOverall} />
          <RatingRow label="Venue / organisation"     avg={stats?.averageVenueOrganization} />
          <RatingRow label="Speakers / experts"       avg={stats?.averageSpeakers} />
          <RatingRow label="Topic"                    avg={stats?.averageTopic} />
        </div>

        {/* NPS breakdown */}
        {stats && stats.total > 0 && (
          <div className="pt-3 border-t border-brand-border grid grid-cols-3 gap-3 text-center">
            <NpsBand label="Detractors" value={stats.detractors} color="bg-brand-orange" />
            <NpsBand label="Passives"   value={stats.passives}   color="bg-yellow-500" />
            <NpsBand label="Promoters"  value={stats.promoters}  color="bg-brand-green" />
          </div>
        )}
      </div>

      {/* Hear-about tally ─────────────────────────────────────────── */}
      {stats && Object.keys(stats.byHearAbout ?? {}).length > 0 && (
        <div className="card p-5">
          <h3 className="text-sm font-semibold text-white mb-3">How they heard about it</h3>
          <div className="space-y-2">
            {Object.entries(stats.byHearAbout)
              .sort((a, b) => b[1] - a[1])
              .map(([k, n]) => {
                const pct = Math.round((n / stats.total) * 100);
                return (
                  <div key={k} className="text-sm">
                    <div className="flex items-center justify-between">
                      <span className="text-white">{hearAboutLabel(k)}</span>
                      <span className="text-brand-muted">
                        {n} <span className="text-brand-muted/70">· {pct}%</span>
                      </span>
                    </div>
                    <div className="mt-1 h-1.5 w-full rounded-full bg-brand-navy overflow-hidden">
                      <div className="h-full bg-brand-green rounded-full" style={{ width: `${pct}%` }} />
                    </div>
                  </div>
                );
              })}
          </div>
        </div>
      )}

      {/* Table of individual submissions ──────────────────────────── */}
      <div className="card overflow-hidden">
        <div className="px-4 py-3 border-b border-brand-border flex items-center justify-between">
          <h3 className="text-sm font-semibold text-white">
            {eventId && selectedEvent
              ? `Submissions · ${selectedEvent.title}`
              : "All submissions"}
          </h3>
          <span className="text-xs text-brand-muted">{rows.length} row{rows.length === 1 ? "" : "s"}</span>
        </div>

        {isLoading ? (
          <TableSkeleton />
        ) : isError ? (
          <div className="p-6 text-sm text-brand-orange">Couldn't load submissions.</div>
        ) : rows.length === 0 ? (
          <div className="p-6 text-sm text-brand-muted">
            No feedback yet. Once attendees submit, their responses will appear here.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="text-left text-brand-muted text-xs uppercase tracking-wide bg-brand-navy/40">
                <tr>
                  <th className="px-4 py-3">Attendee</th>
                  <th className="px-4 py-3">Overall</th>
                  <th className="px-4 py-3">NPS</th>
                  <th className="px-4 py-3">Quote consent</th>
                  <th className="px-4 py-3">Heard about</th>
                  <th className="px-4 py-3">Submitted</th>
                  <th className="px-4 py-3 text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-brand-border">
                {rows.map((r) => (
                  <tr key={r.id} className="hover:bg-white/[0.02]">
                    <td className="px-4 py-3">
                      <div className="text-white flex items-center gap-2">
                        {r.attendee?.fullName ?? "—"}
                        {!r.attendeeId && (
                          <span className="text-[10px] uppercase tracking-wide bg-brand-navy border border-brand-border text-brand-muted px-1.5 py-0.5 rounded">
                            Public
                          </span>
                        )}
                      </div>
                      <div className="text-xs text-brand-muted">
                        {r.attendee?.email ?? r.respondentEmail ?? ""}
                      </div>
                    </td>
                    <td className="px-4 py-3 text-white">{r.ratingOverall}/5</td>
                    <td className="px-4 py-3 text-white">{r.nps}/10</td>
                    <td className="px-4 py-3 text-white">
                      {r.quoteConsent ? <span className="text-brand-green">Yes</span> : <span className="text-brand-muted">No</span>}
                    </td>
                    <td className="px-4 py-3 text-white">{hearAboutLabel(r.hearAbout)}</td>
                    <td className="px-4 py-3 text-brand-muted">{new Date(r.createdAt).toLocaleDateString()}</td>
                    <td className="px-4 py-3 text-right">
                      <div className="inline-flex gap-1">
                        <button
                          type="button"
                          onClick={() => setDetail(r)}
                          className="p-2 rounded-lg hover:bg-white/5 text-white"
                          title="View"
                        >
                          <Quote size={14} />
                        </button>
                        <button
                          type="button"
                          onClick={() => {
                            if (window.confirm("Delete this feedback?")) deleteRow(r.id);
                          }}
                          disabled={isDeleting}
                          className="p-2 rounded-lg hover:bg-brand-orange/20 text-brand-orange"
                          title="Delete"
                        >
                          <Trash2 size={14} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Detail modal ─────────────────────────────────────────────── */}
      {detail && (
        <div
          className="fixed inset-0 z-50 flex items-end sm:items-center justify-center sm:p-6 bg-black/70 backdrop-blur-sm"
          onClick={() => setDetail(null)}
        >
          <div
            className="relative w-full sm:max-w-2xl h-[92vh] sm:h-auto sm:max-h-[90vh] bg-brand-card border border-brand-border rounded-t-2xl sm:rounded-xl flex flex-col"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="p-5 border-b border-brand-border flex items-center justify-between">
              <div>
                <div className="text-white font-semibold">
                  {detail.attendee?.fullName ?? "—"}
                </div>
                <div className="text-xs text-brand-muted">
                  {detail.attendee?.email ?? ""} · {new Date(detail.createdAt).toLocaleString()}
                </div>
              </div>
              <button
                type="button"
                onClick={() => setDetail(null)}
                className="text-brand-muted hover:text-white text-lg"
              >
                ×
              </button>
            </div>
            <div className="flex-1 overflow-y-auto p-5 space-y-4 text-sm">
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                <ScorePill label="Overall"        value={`${detail.ratingOverall}/5`} />
                <ScorePill label="Venue/Org"      value={`${detail.ratingVenueOrganization}/5`} />
                <ScorePill label="Speakers"       value={`${detail.ratingSpeakers}/5`} />
                <ScorePill label="Topic"          value={`${detail.ratingTopic}/5`} />
              </div>
              <ScorePill label="NPS" value={`${detail.nps}/10`} />

              <TextBlock label="Most useful part" value={detail.mostUsefulPart} />
              <TextBlock label="Wanted topics" value={detail.wantedTopics} />
              <TextBlock label="Quote (consent-gated)" value={detail.quote} extra={detail.quoteConsent ? "Consent: Yes" : "Consent: No"} />
              <TextBlock label="Heard about" value={detail.hearAbout === "other" ? detail.hearAboutOther : hearAboutLabel(detail.hearAbout)} />
              <TextBlock label="Someone who should have attended" value={detail.shouldHaveAttended} />
              <TextBlock label="Improvement suggestion" value={detail.improvement} />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Small in-view atoms ──────────────────────────────────────────────────────

function RatingRow({ label, avg }: { label: string; avg?: number }) {
  const v = avg ?? 0;
  return (
    <div>
      <div className="flex items-baseline justify-between">
        <span className="text-sm text-white">{label}</span>
        <span className="text-sm text-white font-semibold">{v.toFixed(1)}</span>
      </div>
      {ratingBar(v)}
    </div>
  );
}

function NpsBand({ label, value, color }: { label: string; value: number; color: string }) {
  return (
    <div className="rounded-lg border border-brand-border p-3 flex flex-col items-center">
      <div className={`${color} w-3 h-3 rounded-full mb-1`} />
      <div className="text-white font-semibold">{value}</div>
      <div className="text-xs text-brand-muted uppercase tracking-wide">{label}</div>
    </div>
  );
}

function ScorePill({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-lg border border-brand-border p-3">
      <div className="text-xs text-brand-muted uppercase tracking-wide">{label}</div>
      <div className="mt-1 text-white font-semibold">{value}</div>
    </div>
  );
}

function TextBlock({
  label,
  value,
  extra,
}: {
  label: string;
  value?: string | null;
  extra?: string;
}) {
  if (!value && !extra) return null;
  return (
    <div>
      <div className="text-xs uppercase tracking-wide text-brand-muted mb-1">{label}</div>
      <div className="rounded-lg bg-brand-navy border border-brand-border p-3 text-white whitespace-pre-wrap">
        {value || "—"}
      </div>
      {extra && <div className="mt-1 text-xs text-brand-muted">{extra}</div>}
    </div>
  );
}
